import { Button, Flex, Switch, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useState } from 'react';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFlags } from 'src/feature-flags/FlagsHook';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { MeetingNoteStepProps } from '../types';

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

const DOCUMENT_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'];

export function NoteAgendaStep({ data, setData }: MeetingNoteStepProps) {
  const { files, isLoading } = useFileUploads({ clientId: data.client?.id });
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const { flagEnabledMeetingNoteSummarisation } = useFlags();
  const [isUploading, setIsUploading] = useState(false);

  const items = filterDocumentFiles(files).map(file => ({
    id: file.id,
    title: file.file.fileName,
    description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
  }));

  const handleSelectionChange = (selectedIds: string[]) => {
    const selectedId = selectedIds[0] || undefined;
    const selectedItem = items.find(i => i.id === selectedId);
    setData(prev => ({
      ...prev,
      selectedAgendaFileId: selectedId,
      selectedAgendaFileName: selectedItem?.title,
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          const uploadedFileName = Array.from(fileList)[0]?.name;
          setData(prev => ({
            ...prev,
            selectedAgendaFileId: newUploadedFileId,
            selectedAgendaFileName: uploadedFileName,
          }));
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the agenda file. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  if (isLoading) return null;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select an agenda for the meeting note">
        <Flex direction="column" gap="md">
          <Flex direction="column" gap="xs" mih={0}>
            <Flex justify="space-between" align="center">
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
                {items.length} {pluralize('document', items.length)} found
              </Text>
              <Button
                component="label"
                variant="subtle"
                size="compact-sm"
                loading={isUploading}
                style={{ cursor: 'pointer' }}
              >
                Upload agenda
                <input
                  type="file"
                  accept={DOCUMENT_FILE_EXTENSIONS.join(',')}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </Button>
            </Flex>
            <SelectableItemList
              items={items}
              hint="The agenda structure will be used to generate the meeting note sections. Client information will be added as supplementary context."
              selectedItemIds={data.selectedAgendaFileId ? [data.selectedAgendaFileId] : []}
              setSelectedItemIds={handleSelectionChange}
              selectMultiple={false}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a meeting agenda (PDF, Word, or text file) using the button above and it
                  will appear here.
                </Text>
              }
            />
          </Flex>
          {flagEnabledMeetingNoteSummarisation && (
            <Flex justify="space-between" align="center" px="xs">
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                Summarise meeting note
              </Text>
              <Switch
                checked={data.summarise}
                onChange={event => {
                  const { checked } = event.currentTarget;
                  setData(prev => ({ ...prev, summarise: checked }));
                }}
              />
            </Flex>
          )}
        </Flex>
      </StepCard>
    </Flex>
  );
}

function filterDocumentFiles(files: UploadedFile[]): UploadedFile[] {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
}
